import { Delete, Edit } from "@mui/icons-material";
import { Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import { AxiosError } from "axios";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { CustomDialog } from "../../../../common/components/CustomDialog";
import CustomLoader from "../../../../common/components/CustomLoader";
import CustomSnackbar from "../../../../common/components/CustomSnackbar";
import CustomTabs from "../../../../common/components/CustomTabs";
import { getForm } from "../../../../common/config/forms";
import { useAuth } from "../../../../common/hooks/useAuth";
import { IBeehive, IBeehiveBody } from "../../../../common/interfaces/beehive";
import { ICustomDialogProps } from "../../../../common/interfaces/dialog";
import { ICustomOption } from "../../../../common/interfaces/form";
import { ACTIONS } from "../../../../common/interfaces/table";
import { ITab } from "../../../../common/interfaces/tabs";
import beehiveService from "../../../../common/services/beehiveService";
import userService from "../../../../common/services/userService";
import AlertsTab from "./components/AlertsTab";
import ChartsTab from "./components/ChartsTab";
import StatsTab from "./components/StatsTab";

interface IBeehiveDetailsProps { }

const BeehiveDetails: FC<IBeehiveDetailsProps> = () => {

    // hooks 
    const { permissionsData, loginData, permissionsState } = useAuth();
    const params = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const theme = useTheme();
    const largeMediumScreen = useMediaQuery(theme.breakpoints.up("md"));
    const smallScreen = useMediaQuery(theme.breakpoints.up("sm"))

    // form
    const [action, setAction] = useState<ACTIONS | undefined>(undefined);
    const addBeehiveForm = getForm<IBeehiveBody>("addBeehive");
    const [usersOptions, setUsersOptions] = useState<ICustomOption[]>([]);

    // dialog
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogBody, setDialogBody] = useState<React.ReactNode>(<></>);

    // beehives/stats states
    const [currentBeehive, setCurrentBeehive] = useState<IBeehive | undefined>(undefined)

    // tabs
    const [_activeTab, setActiveTab] = useState<number>(0);

    // snackbar
    const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
    const [messageSnackbar, setMessageSnackbar] = useState<string>("");
    const [responseType, setResponseType] = useState<"success" | "error">("success");

    const getCurrentBeehive = async (id: string) => {
        try {
            const response = await beehiveService.get(id)
            setCurrentBeehive(response.content)
        }
        catch (error) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.title)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        }
    }

    const getUsersOptionsList = async () => {
        try {
            const response = await userService.getAll();
            let options: ICustomOption[] = response.content.map(user => {
                return {
                    desc: user.emailAddress,
                    value: user.id
                }
            })
            setUsersOptions(options);
        }
        catch (error) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.title)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        }
    }

    useEffect(() => {
        if (params.id)
            getCurrentBeehive(params.id)
    }, [params.id])

    const handleAction = (action: ACTIONS, beehive: IBeehive | undefined) => {
        setAction(action)
        setCurrentBeehive(beehive);
        switch (action) {
            case "delete": {
                if (beehive) {
                    setDialogTitle(t("beehiveDetailsPage.deleteDialogTitle") + '"' + beehive.name + '"?');
                    setDialogBody(
                        <Typography variant="body1">
                            {t("beehiveDetailsPage.deleteDialogWarningText")}
                        </Typography>
                    );
                    setOpenDialog(true);
                }
                break;
            }
            case "edit": {
                if (beehive) {
                    getUsersOptionsList()
                    setDialogTitle(t("beehiveDetailsPage.editDialogTitle") + '"' + beehive.name + '"');
                    setDialogBody(undefined);
                    setOpenDialog(true);
                }
                break;
            }
            default: {
                break;
            }
        };
    };

    // close dialog handler
    const handleClose = () => setOpenDialog(false);

    // confirm dialog handler
    const handleActionConfirm = async (data: IBeehiveBody | undefined) => {
        handleClose();
        try {
            const response = await apiRequest(data);
            if (action === "delete")
                navigate("/beehives")
            if (action === "edit")
                getCurrentBeehive(response.content.id)
            setOpenSnackbar(true);
            setResponseType("success");
            setMessageSnackbar(t("genericSuccess"));
        } catch (error: unknown) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.content.message)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        }
    }

    // api request handler
    const apiRequest = (data: IBeehiveBody | undefined) => {
        if (data && action === "edit" && currentBeehive) {
            return beehiveService.put(currentBeehive.id, data)
        } else if (action === "delete" && currentBeehive) {
            return beehiveService.delete(currentBeehive.id)
        } else {
            return Promise.reject()
        }
    }

    // dialog props
    const dialogProps: ICustomDialogProps = {
        type: action === "delete" ? "confirm" : "form",
        close: handleClose,
        confirm: () => handleActionConfirm(undefined),
        title: dialogTitle,
        open: openDialog,
        confirmButtonLabel: action === "delete" ? t("beehiveDetailsPage.dialogConfirmButton") : t("beehiveDetailsPage.dialogSaveButton"),
        closeButtonLabel: t("beehiveDetailsPage.dialogCloseButton"),
        fullWidth: true,
        formProps: {
            ...addBeehiveForm,
            inputs: [
                ...addBeehiveForm.inputs.map(input => {
                    if (input.name === "ownerId")
                        return { ...input, options: usersOptions }
                    else
                        return input
                })
            ],
            initialValues: { ...currentBeehive },
            isDialog: true,
            submit: (data) => handleActionConfirm(data),
            customStyles: {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }
        }
    };

    const getViewPermissions = () => {
        return permissionsState &&
            permissionsState.existViewAlertsPermission &&
            permissionsData &&
            (
                permissionsData['bhalerts'].includes('bhalerts_view_all') ||
                (
                    permissionsData['bhalerts'].includes("bhalerts_view_only_area") &&
                    loginData &&
                    currentBeehive &&
                    loginData.content.user.managedAreas.find(area => area.id === currentBeehive.owner.areaId)
                ) || (
                    permissionsData['bhalerts'].includes('bhalerts_view_only_owned') &&
                    loginData &&
                    currentBeehive &&
                    currentBeehive.ownerId === loginData.content.user.id
                )
            )
    }

    const setTabs = () => {
        let showTabs: ITab[] = []
        if (getViewPermissions()) {
            showTabs.push({
                label: "beehiveDetailsPage.tabs.alertsTabLabel",
                index: 2,
                content: <AlertsTab beehiveId={currentBeehive?.id} currentBeehive={currentBeehive} parentDialogOpen={openDialog} />
            })
        }

        if (
            permissionsState &&
            permissionsState.existViewBeehivesPermission
        ) {
            showTabs.push(
                {
                    label: "beehiveDetailsPage.tabs.statsTabLabel",
                    index: 1,
                    content: <StatsTab beehiveId={currentBeehive?.id} beehiveData={currentBeehive} parentDialogOpen={openDialog} />
                }
            )
        }

        showTabs.push(
            {
                label: "beehiveDetailsPage.tabs.chartsTabLabel",
                index: 0,
                content: <ChartsTab beehiveId={currentBeehive?.id} parentDialogOpen={openDialog} beehive={currentBeehive} />
            },
        )

        showTabs.sort((tab1, tab2) => tab1.index - tab2.index)

        return showTabs;
    }

    const getEditPermissions = () => {
        return permissionsState &&
            permissionsState.existEditBeehivesPermission &&
            permissionsData &&
            (
                permissionsData['bh'].includes('bh_edit_all') ||
                (permissionsData['bh'].includes('bh_view_all') && (
                    permissionsData['bh'].includes("bh_edit_only_area") &&
                    loginData &&
                    currentBeehive &&
                    loginData.content.user.managedAreas.find(area => area.id === currentBeehive.owner.areaId)
                ) || (
                        permissionsData['bh'].includes('bh_edit_only_owned') &&
                        loginData &&
                        currentBeehive &&
                        currentBeehive.ownerId === loginData.content.user.id
                    ))
            )
    }

    const getDeletePermissions = () => {
        return permissionsState &&
            permissionsState.existEditBeehivesPermission &&
            permissionsData &&
            (
                permissionsData['bh'].includes('bh_delete_all') ||
                (permissionsData['bh'].includes('bh_view_all') && (
                    permissionsData['bh'].includes("bh_delete_only_area") &&
                    loginData &&
                    currentBeehive &&
                    loginData.content.user.managedAreas.find(area => area.id === currentBeehive.owner.areaId)
                ) || (
                        permissionsData['bh'].includes('bh_delete_only_owned') &&
                        loginData &&
                        currentBeehive &&
                        currentBeehive.ownerId === loginData.content.user.id
                    ))
            )
    }

    return <Box sx={{ py: 1, px: 2, minHeight: "calc(100vh - 198px)" }}>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mb: 2 }}>
            {t("navigateBack")}
        </Button>
        <CustomSnackbar
            type={responseType}
            message={messageSnackbar}
            open={openSnackbar}
            onClose={() => setOpenSnackbar(false)}
        />
        <CustomDialog {...dialogProps}>{dialogBody}</CustomDialog>
        {
            !currentBeehive ? !openDialog && <CustomLoader /> :
                <>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: largeMediumScreen ? 2 : 1 }}>
                        <Box sx={{ mb: 2, display: "flex", flexDirection: smallScreen ? "row" : "column" }}>
                            <Typography variant="h6" sx={{ mr: 1 }}>{t("beehiveDetailsPage.title")}</Typography>
                            <Typography variant="h6" color="primary">{currentBeehive.name}</Typography>
                        </Box>
                        <Box sx={{ mb: largeMediumScreen ? 2 : 0, display: "flex", flexDirection: largeMediumScreen ? "row" : "column" }}>
                            {
                                getEditPermissions() &&
                                <Button variant="contained" onClick={() => handleAction(ACTIONS.EDIT, currentBeehive)}
                                    sx={(theme) => ({
                                        [theme.breakpoints.only("xs")]: {
                                            minWidth: { xs: 0 },
                                            width: { xs: 34 },
                                            height: { xs: 32 },
                                            padding: { xs: 2 }
                                        }
                                    })}
                                >
                                    <Edit sx={{ mr: smallScreen ? 0.5 : 0 }} />
                                    {smallScreen && t("beehiveDetailsPage.editButtonLabel")}
                                </Button>
                            }
                            {
                                getDeletePermissions() &&
                                <Button color="error" variant="contained"
                                    sx={(theme) => ({
                                        [theme.breakpoints.only("xs")]: {
                                            minWidth: { xs: 0 },
                                            width: { xs: 34 },
                                            height: { xs: 32 },
                                            padding: { xs: 2 }
                                        },
                                        ml: largeMediumScreen ? 2 : 0,
                                        mt: largeMediumScreen ? 0 : 1
                                    })}
                                    onClick={() => handleAction(ACTIONS.DELETE, currentBeehive)}
                                >
                                    <Delete sx={{ mr: smallScreen ? 0.5 : 0 }} />
                                    {smallScreen && t("beehiveDetailsPage.deleteButtonLabel")}
                                </Button>
                            }
                        </Box>
                    </Box>

                    {
                        currentBeehive && <Box sx={{ minHeight: "calc(100vh - 232px)" }}>
                            <CustomTabs
                                setActiveTab={(tab: ITab) => setActiveTab(tab.index)}
                                tabs={setTabs()}
                                level={"first"}
                                variant="standard"
                            />
                        </Box>
                    }
                </>
        }
    </Box>
}

export default BeehiveDetails;